import {
  AxiosException,
  useAxiosBackendProvider,
} from "@acin.reactjs/acin-ui/axios-backend-provider";
import { BackendConfig } from "@acin.reactjs/acin-ui/baseApp";
import { isAxiosException } from "@acin.reactjs/acin-ui/routerTabs";
import { AxiosHeaders, AxiosResponse, isAxiosError } from "axios";
import { getI18n } from "react-i18next";
import { FallbackLng } from "../../constants/AppConstants";
import { LoginPageRoute } from "../../constants/AppRoutes";
import { downloadResponseAsFile } from "../../helpers/FilesHelper";
import {
  AppBackendAPI,
  AppBackendProviderProps,
  BaseErrorResponse,
  RawResponse,
  ResponseError,
} from "../../interfaces/BackendInterfaces";
import { AppParamsProps, Session } from "../../interfaces/BaseAppInterfaces";
import { handleBackendErrors } from "../form/FormUtils";
import {
  checkSessionToken,
  isSessionExpired,
  setSessionExpired,
} from "../session/sessionConfig";

type AppBackendConfig = BackendConfig<
  AppParamsProps,
  Session,
  AppBackendAPI,
  AppBackendProviderProps
>;

// missing request to register error on the backend
// eslint-disable-next-line @typescript-eslint/require-await
const onClientError: AppBackendConfig["onClientError"] = async () => null;

// should catch error on the headers and pass it here
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const onServerError: AppBackendConfig["onServerError"] = (response) => {
  const parsedResponse: AxiosResponse<BaseErrorResponse> = response as never;

  if (parsedResponse.status === 500) {
    return parsedResponse.data?.error?.code || null;
  }

  return null;
};

const onRequest: NonNullable<AppBackendProviderProps["onRequest"]> = async (
  request,
  context,
) => {
  // Public endpoints that must always be allowed through regardless of session state
  const publicEndpoints = ["login", "forgot-password", "reset-password"];
  const isPublicEndpoint = publicEndpoints.some((ep) =>
    request.url?.includes(ep),
  );

  // Immediately reject any non-public request if session is already known to be expired
  if (!isPublicEndpoint && isSessionExpired()) {
    throw new Error("SESSION_EXPIRED");
  }

  const {
    state: {
      session: { params: { auth: { token } = {}, user } = {} },
      language,
    },
  } = context;

  if (request.downloadResponse) {
    request.responseType = "blob";
  }

  request.headers = {
    ...request.headers,
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(!user?.admin &&
      user?.selectedEntity?.code && {
        "Entity-Code": user.selectedEntity.code,
      }),
    "Accept-language": language || FallbackLng,
  };

  if (request.url?.endsWith("refreshToken")) {
    return request;
  }

  const authToken = await checkSessionToken(context);

  if (authToken) {
    request.headers = {
      ...request.headers,
      Authorization: `Bearer ${authToken}`,
    };
  } else if (!isPublicEndpoint && isSessionExpired()) {
    // Session is expired and refresh failed — cancel the request
    // by returning a rejected promise / throwing a cancellation
    throw new Error("SESSION_EXPIRED");
  } else {
    delete request.headers?.Authorization;
  }

  if (request.authenticationToken) {
    request.headers = {
      ...request.headers,
      Authorization: `Bearer ${request.authenticationToken}`,
    };
  }

  return request;
};

const onRequestResponse: AppBackendProviderProps["onRequestResponse"] = (
  promise,
  {
    setError,
    downloadResponse,
    skipErrorMessage,
    dontTriggerUnauthorizedException,
  },
  { alerts },
) =>
  promise
    .then(({ data, headers, status }) => {
      // treat as file, lets download it, and return null
      if (downloadResponse) {
        downloadResponseAsFile(
          data as Blob | undefined,
          headers as AxiosHeaders,
        );
        return true;
      }

      if (data instanceof Blob) {
        throw new Error("Invalid response type blob");
      }

      if (status === 204 && !data) {
        return true;
      }

      if ("totalCount" in data) {
        return {
          data: data.data,
          totalCount: data.totalCount,
        };
      }

      if ("error" in data) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw data.error;
      }

      return data.data || data || true;
    })
    .catch((response: AxiosException<RawResponse<unknown>> | ResponseError) => {
      let errorResponse: ResponseError | undefined;

      if (isAxiosException<RawResponse<unknown>>(response)) {
        const errorCode = response.error?.status;

        if (
          errorCode === 404 ||
          errorCode === 403 ||
          errorCode === 500 ||
          (errorCode === 401 && dontTriggerUnauthorizedException)
        ) {
          // eslint-disable-next-line @typescript-eslint/no-throw-literal
          throw response;
        }

        // 401 without dontTriggerUnauthorizedException: session expired.
        // Framework interceptor already dispatched logout/redirect.
        // Suppress error alert to avoid multiple "Não autorizado" messages.
        if (errorCode === 401) {
          setSessionExpired();
          return false;
        }

        const rawResponse = response.error.response?.data;

        if (rawResponse && "error" in rawResponse) {
          errorResponse = rawResponse.error;
        } else {
          errorResponse = undefined;
        }
      } else {
        errorResponse = response;
      }

      if (!errorResponse) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw response;
      }

      const { message, errors } = errorResponse;

      if (!skipErrorMessage) {
        handleBackendErrors(errors, alerts, message, setError);
      }

      if (errors) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw errors;
      }

      return false;
    });

const onUnknownError: NonNullable<AppBackendConfig["onUnknownError"]> = (
  error,
  { alerts },
) => {
  const { t } = getI18n();

  // Silently suppress errors from cancelled requests due to expired session
  if (error instanceof Error && error.message === "SESSION_EXPIRED") {
    return false;
  }

  if (isAxiosError(error) && error.response?.status === 429) {
    alerts.error(t("errors.tooManyRequests"));
    return false;
  }

  return null;
};

const backendConfig: AppBackendConfig = {
  backendProvider: useAxiosBackendProvider,
  backendProviderProps: {
    onRequest,
    onRequestResponse,
    clearCacheOnUnmount: true,
  },
  baseUrl: `${import.meta.env.VITE_BASE_URL as string}/api`,
  loginPath: LoginPageRoute,
  backOfficePath: LoginPageRoute,
  onClientError,
  onServerError,
  onUnknownError,
};

export default backendConfig;
